'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import {
  SingleLineFragment,
  BooleanFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { useLocalization } from '@/ui/providers/localizationProvider';

export type FilloutEmbedTile = {
  'fillout-id': SingleLineFragment;
  'inherit-parameters': BooleanFragment;
  'show-email': BooleanFragment;
  'show-chat': BooleanFragment;
  'show-phone': BooleanFragment;
  'show-callback': BooleanFragment;
};

// Dynamic import with ssr: false avoids hydration mismatch (same pattern as Video tile's ReactPlayer)
const FilloutStandardEmbed = dynamic(
  () => import('@fillout/react').then((mod) => mod.FilloutStandardEmbed),
  { ssr: false },
);

export default function FilloutEmbed({ tile }: Readonly<{ tile: FilloutEmbedTile }>) {
  const filloutId = tile['fillout-id']?.text;
  const { language, country } = useLocalization();
  const searchParams = useSearchParams();

  if (!filloutId) {
    return null;
  }

  const showEmail = tile['show-email']?.value ?? false;
  const showChat = tile['show-chat']?.value ?? false;
  const showPhone = tile['show-phone']?.value ?? false;
  const showCallback = tile['show-callback']?.value ?? false;

  // Apps link to landing page with the nukiId in decimal, but the upgrade-program
  // direct-purchase link expects hex, so convert before handing it to Fillout (the form
  // builds its redirect URL from this parameter).
  const nukiIdDecimal = searchParams.get('nukiId');
  const nukiIdHex =
    nukiIdDecimal && !Number.isNaN(Number(nukiIdDecimal))
      ? Number(nukiIdDecimal).toString(16).toUpperCase()
      : undefined;

  return (
    <FilloutStandardEmbed
      filloutId={filloutId}
      dynamicResize
      inheritParameters
      parameters={{
        lang: language,
        country,
        email: String(showEmail),
        chat: String(showChat),
        phone: String(showPhone),
        callback: String(showCallback),
        ...(nukiIdHex ? { nukiId: nukiIdHex } : {}),
      }}
    />
  );
}
